import { Fragment, type ReactNode } from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { TableHeader } from '@/components/table/TableHeader';
import type { ColumnConfig } from '@/components/table/Table';
import type { SelectedSort } from '@/hooks/useSortableData';
import {
  ROUTES_TABLE_BODY_ACTIONS_CELL_SX,
  ROUTES_TABLE_BODY_CELL_SX,
  ROUTES_TABLE_HEADER_CELL_SX,
} from '../../constants/routesTable.constants';
import type { RouteTableRowData } from '../../types/routeTable';
import { getRouteTableRowField } from '../../utils/getRouteTableRowField';
import RouteCardExpanded from '../RouteCardExpanded';

interface ExpandableRoutesTableProps {
  columns: ColumnConfig[];
  rows: RouteTableRowData[];
  expandedIds: Set<string>;
  selectedSort: SelectedSort;
  handleSort: (key: string) => void;
  renderActions: (row: RouteTableRowData) => ReactNode;
  syncVisitSearchFromRouteShippingNumber?: string;
}

const ExpandableRoutesTable = ({
  columns,
  rows,
  expandedIds,
  selectedSort,
  handleSort,
  renderActions,
  syncVisitSearchFromRouteShippingNumber,
}: ExpandableRoutesTableProps) => (
  <MuiTable>
    <TableHeader
      columns={columns}
      renderActions={(_rowData: Record<string, unknown>) => null}
      selectedSort={selectedSort}
      handleSort={handleSort}
      headerCellSx={ROUTES_TABLE_HEADER_CELL_SX}
    />
    <TableBody>
      {rows.map((row, rowIndex) => (
        <Fragment key={row.id}>
          <TableRow
            style={{
              backgroundColor:
                rowIndex % 2 === 0
                  ? 'var(--light-gray)'
                  : 'var(--white-color)',
            }}
          >
            {columns.map((column) => {
              const raw = getRouteTableRowField(row, column.field);
              return (
                <TableCell key={column.field} sx={ROUTES_TABLE_BODY_CELL_SX}>
                  {column.render
                    ? column.render(raw, row)
                    : String(raw ?? '')}
                </TableCell>
              );
            })}
            <TableCell sx={ROUTES_TABLE_BODY_ACTIONS_CELL_SX}>
              {renderActions(row)}
            </TableCell>
          </TableRow>
          {expandedIds.has(row.id) && (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                sx={{
                  p: 0,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <div className='border-t border-gray-200 bg-gray-50 p-4'>
                  <RouteCardExpanded
                    routeId={row.id}
                    syncVisitSearchFromRouteShippingNumber={
                      syncVisitSearchFromRouteShippingNumber
                    }
                  />
                </div>
              </TableCell>
            </TableRow>
          )}
        </Fragment>
      ))}
    </TableBody>
  </MuiTable>
);

export default ExpandableRoutesTable;
